import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { PublicHome } from "@/pages/PublicHome";
import { AdminLogin } from "@/pages/AdminLogin";
import { QuotationView } from "@/pages/QuotationView";
import { QuotationsPage } from "@/pages/QuotationsPage";
import { Reports } from "@/pages/Reports";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { supabase } from "./integrations/supabase/client";
import { SidebarProvider, useSidebar } from "@/components/ui/sidebar";
import Index from "@/pages/Index";
import { StockManagement } from "@/components/StockManagement";
import { StockConfig } from "@/components/StockConfig";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Session } from "@supabase/supabase-js";
import { useToast } from "./hooks/use-toast";
import { PasswordChangeDialog } from "@/components/ui/password-change-dialog";
import { MobileLayout } from "@/components/mobile/MobileLayout";

const AdminLayout = () => {
  const { toggleSidebar } = useSidebar();
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  // Check if the app is already installed
  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }
  }, []);

  // Capture install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    // Show prompt
    deferredPrompt.prompt();
    
    // Wait for user choice
    const choiceResult = await deferredPrompt.userChoice;
    
    if (choiceResult.outcome === 'accepted') {
      toast({
        title: "Instalação iniciada",
        description: "O aplicativo está sendo instalado no seu dispositivo",
      });
      setIsInstalled(true);
    }
    
    // Clear the deferredPrompt variable
    setDeferredPrompt(null);
  };

  // Mobile users now use the MobileLayout component
  if (isMobile) {
    return <MobileLayout />;
  }

  return (
    <div className="flex min-h-screen w-full bg-background">
      <DashboardSidebar />
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        <main className="flex-1 overflow-y-auto">
          {/* Add download button to the top right */}
          {!isInstalled && deferredPrompt && (
            <div className="fixed top-4 right-4 z-50">
              <Button
                variant="outline"
                className="flex items-center gap-2 border-[#0073B7] text-[#0073B7] hover:bg-[#0073B7]/10"
                onClick={handleInstallClick}
              >
                <Download className="h-4 w-4" />
                Baixar Aplicativo
              </Button>
            </div>
          )}
          <div className="w-full">
            <Routes>
              <Route path="dashboard" element={<Index />} />
              <Route path="stock" element={<StockManagement />} />
              <Route path="reports" element={<Reports />} />
              <Route path="quotations" element={<QuotationsPage />} />
              <Route path="settings" element={<StockConfig />} />
              <Route path="/" element={<Navigate to="dashboard" replace />} />
              <Route path="*" element={<Navigate to="dashboard" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
};

function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Clear service worker cache on app load
  useEffect(() => {
    // Clear service worker cache for quotations on startup
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      console.log("App: Clearing quotations cache on startup");
      navigator.serviceWorker.controller.postMessage({
        type: 'CLEAR_QUOTATIONS_CACHE'
      });
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      
      // Check if user needs to change password
      if (session?.user?.user_metadata?.first_access) {
        setShowPasswordChange(true);
      }
      
      setIsLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);

      // Check for first_access flag when auth state changes
      if (event === 'SIGNED_IN' && session?.user?.user_metadata?.first_access) {
        setShowPasswordChange(true);
      }

      if (event === 'SIGNED_IN') {
        toast({
          title: "Login realizado com sucesso",
          description: "Bem-vindo ao painel administrativo!",
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [toast]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0073B7]"></div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<PublicHome />} />
        <Route 
          path="/auth" 
          element={session ? <Navigate to="/adm/dashboard" replace /> : <AdminLogin />} 
        />
        {/* Make quotation view available globally at both paths for compatibility */}
        <Route path="/quotation/:code" element={<QuotationView />} />
        <Route path="quotation/:code" element={<QuotationView />} />
        <Route
          path="/adm/*"
          element={
            session ? (
              <SidebarProvider>
                <AdminLayout />
                {showPasswordChange && (
                  <PasswordChangeDialog 
                    isOpen={showPasswordChange} 
                    onClose={() => setShowPasswordChange(false)} 
                  />
                )}
              </SidebarProvider>
            ) : (
              <Navigate to="/auth" replace />
            )
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
    </BrowserRouter>
  );
}

export default App;